import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AuthService } from 'app/core/auth/auth.service';
import { MethodResponse } from 'app/models/base-models';
import { AccountService } from 'app/services/account.service';
import { of, pipe, tap } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

export const ChangeMyPasswordStore = signalStore(
    { providedIn: 'root' },
    withState(() => ({
        isLoading: false,
        success: false,
        error: null as string | null,
        userName: null as string | null,
        // dirty states
        isChangePasswordFormDirty: false,

        // valid states
        isChangePasswordFormValid: false,

        // form data
        changePasswordForm: null as any,

        showAlert: false,
        alert: {
            type: 'success',
            title: '',
            message: '',
        },
    })),
    withMethods((store) => ({
        updateChangePasswordForm(data: any, isValid: boolean, isDirty: boolean) {
            patchState(store, {
                changePasswordForm: data,
                isChangePasswordFormDirty: isDirty,
                isChangePasswordFormValid: isValid,
            });
        },
        setShowAlert(showAlert: boolean) {
            patchState(store, {
                showAlert: showAlert,
            });
        },
        showErrorAlertMessage(message: string) {
            patchState(store, {
                showAlert: true,
                alert: { type: 'error', title: '', message: message },
            });
        },
        showSuccessAlertMessage() {
            patchState(store, {
                showAlert: true,
                alert: {
                    type: 'success',
                    title: 'Thank you!',
                    message: 'If these details match our records, instructions will be sent via email.',
                },
            });
        },
        resetDirtyState() {
            patchState(store, {
                isChangePasswordFormDirty: false,
            });
        },
        resetState() {
            patchState(store, {
                userName:'',
                isLoading: false,
                showAlert: false,
                alert: { type: 'success', title: '', message: '' },
            });
        },
    })),
    withMethods(
        (
            store,
            authService = inject(AuthService),
            accountService = inject(AccountService),
            router = inject(Router),
            snackbar = inject(MatSnackBar)
        ) => {
            return {
                changePassword: rxMethod<void>(
                    pipe(
                        tap(() =>
                            patchState(store, {
                                isLoading: true,
                                showAlert: false,
                            })
                        ),
                        switchMap(() =>
                            authService.validateCurrentPassword(store.changePasswordForm().currentPassword).pipe(
                                tapResponse({
                                    next: (response: MethodResponse) => {
                                        if (response.isError) {
                                            patchState(store, {
                                                isLoading: false,
                                                showAlert: true,
                                                alert: {
                                                    type: 'error',
                                                    title: '',
                                                    message: response.message,
                                                },
                                            });

                                            return;
                                        }

                                        return authService
                                            .changeMyPassword({
                                                password: store.changePasswordForm().newPassword,
                                                confirmPassword: store.changePasswordForm().confirmNewPassword,
                                            })
                                            .subscribe({
                                                next: (response: MethodResponse) => {
                                                    if (response.isError) {
                                                        patchState(store, {
                                                            isLoading: false,
                                                            showAlert: true,
                                                            alert: {
                                                                type: 'error',
                                                                title: '',
                                                                message: response.message,
                                                            },
                                                        });
                                                        return;
                                                    } else {
                                                        snackbar.open('Your password has been successfully updated. Please login in now using your new password.', 'Close', {
                                                            verticalPosition: 'top',
                                                            duration: 3000,
                                                        });

                                                        // signout user and redirect to sign-in page
                                                        authService.signOut().subscribe(() => {
                                                            router.navigate(['/sign-in'], {replaceUrl: true});
                                                        });
                                                    }
                                                },
                                            });
                                    },
                                    error: (error: any) => {
                                        patchState(store, {
                                            isLoading: false,
                                            showAlert: true,
                                            alert: {
                                                type: 'error',
                                                title: '',
                                                message: 'Password change has failed. Please try again later.',
                                            },                                        
                                        });
                                    },
                                })
                            )
                        )
                    )
                ),
                loadUserName: rxMethod<void>(
                    pipe(
                        switchMap(() => {                        

                            return accountService.getMyAccountDetails().pipe(
                                tap((response) => {
                                    patchState(store, { userName: response.username });
                                }),
                                take(1)
                            );
                        })
                    )
                ),
            };
        }
    )
);
